//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { clamp } from '@utils/clamp';

/**
 *
 * Returns the sum of all the elements within a numeric array
 *
 * @param arr A numeric array
 */
export function sum(arr: number[]) {
  return arr.reduce((a, value) => a + value, 0);
}

/**
 *
 * Alphabetizes an array of objects using localeCompare()
 *
 * @param getter A function that, given one of the items in the array, returns the field to sort on
 */
export function alphabetizeLocaleCompare<T>(
  arr: T[],
  getter: (o: T) => string,
): T[] {
  return arr.sort((a, b): number => {
    return getter(a).toLowerCase().localeCompare(getter(b).toLowerCase());
  });
}

/**
 * Returns a new array containing only items that appear in both given arrays.
 */
export function intersection<T>(arr1: T[], arr2: T[]): T[] {
  const arr2Set = new Set(arr2);
  return arr1.filter((item) => arr2Set.has(item));
}

/**
 * Checks if 2 arrays have the same elements in any order, doesn't deep compare objects
 */
export function arrayUnorderedEquals<T>(arr1: T[], arr2: T[]): boolean {
  if (arr1.length !== arr2.length) {
    return false;
  }

  if (arr1.length === 0 && arr2.length === 0) {
    return true;
  }

  return intersection(arr1, arr2).length === arr1.length;
}

/**
 * Returns a new array containing the items from the first array that do not appear in the second.
 */
export function difference<T>(arr1: T[], arr2: T[]): T[] {
  const arr2Set = new Set(arr2);
  return arr1.filter((item) => !arr2Set.has(item));
}

/**
 * Returns a new array with the item at the provided currIndex moved to the provided newIndex
 *
 * @param arr
 * @param currIndex The current index of the item to be moved
 * @param newIndex The new index the item will be moved to
 */
export function move<T>(arr: T[], currIndex: number, newIndex: number): T[] {
  if (currIndex < 0 || currIndex >= arr.length) {
    return arr;
  }

  const safeNewIndex = clamp(newIndex, 0, arr.length - 1);

  const reorderedArray = [...arr];
  reorderedArray.splice(
    safeNewIndex,
    0,
    reorderedArray.splice(currIndex, 1)[0],
  );
  return reorderedArray;
}

type ExtractBaseTypes<T> = T extends unknown[] ? T[number] : never;

type ToTuple<T extends unknown[][]> = {
  [idx in keyof T]: ExtractBaseTypes<T[idx]>;
};

/**
 * Returns an array of grouped elements, the first of which contains the first elements of the given
 * arrays, the second of which contains the second elements of the given arrays, etc.
 *
 * Based on the underscore `zip` method.
 */
export function zip<T, U extends unknown[][]>(
  arr: T[],
  ...otherArrs: U
): [T, ...ToTuple<U>][] {
  return arr.map(
    (value, idx) =>
      [value, ...otherArrs.map((arr) => arr[idx])] as [T, ...ToTuple<U>],
  );
}

/**
 * Takes a predicate and a list of values and returns a tuple (2-item array),
 * with each item containing the subset of the list that matches the predicate
 * and the complement of the predicate respectively
 *
 * @sig (T -> Boolean, T[]) -> [T[], T[]]
 *
 * @param {Function} predicate A predicate to determine which side the element belongs to.
 * @param {Array} arr The list to partition
 *
 * Inspired by the Ramda function of the same name
 * @see https://ramdajs.com/docs/#partition
 *
 * @example
 *     const isNegative: (n: number) => boolean = n => n < 0
 *     const numbers = [1, 2, -4, -7, 4, 22]
 *     partition(isNegative, numbers)
 *     // => [ [-4, -7], [1, 2, 4, 22] ]
 */
export function partition<T>(
  predicate: (val: T) => boolean,
  arr: Array<T>,
): [Array<T>, Array<T>] {
  const partitioned: [Array<T>, Array<T>] = [[], []];
  arr.forEach((val: T) => {
    const partitionIndex: 0 | 1 = predicate(val) ? 0 : 1;
    partitioned[partitionIndex].push(val);
  });
  return partitioned;
}

/**
 * Creates a duplicate-free version of an array
 * @param items Array of items to deduplicate
 */
export function uniq<T>(items: T[]): T[] {
  return Array.from(new Set(items));
}
